import Moment from 'moment';
import React from 'react';

// components
import { Box, ThemeProvider, Typography } from '@mui/material';
import { styled } from '@mui/system';

import { legal } from '../../i18n/constants';
import type { Languages } from '../../i18n/i18n.language-config';
import { languages } from '../../i18n/i18n.language-config';
import darkTheme from '../../themes/dark.theme';
import { usePlatformLanguage } from '../../utils/hooks/usePlatformLanguage';
import CbButton from '../Buttons/CbButton';
import LogoHeader from './LogoHeader';

interface Props {
  children: React.ReactNode;
  MarketingSlot?: React.ReactElement | null;
}

function NextPublicWrapper({ children, MarketingSlot = null }: Props) {
  const region = usePlatformLanguage();

  return (
    <ThemeProvider theme={darkTheme}>
      <Background>
        <LoginContainer>
          <ContentBackground>
            <ContentContainer>
              <Box maxWidth="45%">
                <LogoHeader />
              </Box>
              <Box pt={3}>{children}</Box>
            </ContentContainer>
            <MarketingContainer>
              {MarketingSlot ? (
                React.cloneElement(MarketingSlot)
              ) : (
                <MarketingStylesContainer>
                  <ResponsiveImageBox>
                    <img
                      alt="person-working"
                      src="/images/LoginBackground.png"
                    />
                  </ResponsiveImageBox>
                  <MarketingMessage>
                    <Typography className="canon-text" component="h3">
                      Adaptive Cyber Insurance
                    </Typography>
                    <Typography
                      className="tertia-text"
                      style={{ marginBottom: '1rem' }}
                    >
                      Cowbell makes digital protection clear and attainable -
                      simplifying cyber insurance so businesses can move forward
                      with quiet confidence.
                    </Typography>
                    <MarketingAction
                      stylename="primary"
                      fixed
                      href={LEARN_MORE_LINK[region]}
                      target="_blank"
                    >
                      Learn more
                    </MarketingAction>
                  </MarketingMessage>
                </MarketingStylesContainer>
              )}
            </MarketingContainer>
          </ContentBackground>
        </LoginContainer>
        <Footer region={region} />
      </Background>
    </ThemeProvider>
  );
}

export default NextPublicWrapper;

const LEARN_MORE_LINK = {
  [languages['en-US']]: 'https://cowbell.insure',
  [languages['en-GB']]: 'https://cowbell.insure/uk',
  [languages['en-AU']]: 'https://cowbell.insure', // TODO: No AU specific link exists in portal yet, update this when it exists
};

const Background = styled(Box)(({ theme }: { theme: any }) => {
  return {
    width: '100%',
    background: '#525461',
    overflow: 'hidden',
    color: theme.palette.text.contrastText,
  };
});

const Footer = ({ region }: { region: Languages }) => {
  return (
    <span
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 5,
        transform: 'translateX(-50%)',
        textAlign: 'center',
        fontSize: '0.83rem',
        width: '100%',
      }}
    >
      <a
        href="https://cowbell.insure/terms/"
        rel="noopener noreferrer"
        target="_blank"
        style={{ color: 'inherit' }}
      >
        Terms of Service
      </a>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <a
        href="https://cowbell.insure/privacy/"
        rel="noopener noreferrer"
        target="_blank"
        style={{ color: 'inherit' }}
      >
        Privacy Policy
      </a>
      <span style={{ marginLeft: 5 }}>
        {`© ${Moment().year()} Cowbell Cyber Inc. All Rights Reserved, `}
        {legal[region]}
      </span>
    </span>
  );
};

const LoginContainer = styled('div')(() => {
  return {
    display: 'flex',
    width: '100vw',
    maxWidth: '1440px',
    height: '100vh',
    margin: '0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#525461',
    color: '#fff',
  };
});

const ContentBackground = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '75%',
  height: '80%',
  maxHeight: '980px',
  justifyContent: 'start',
  position: 'relative',
  background: '#1d1d35',
  borderRadius: 8,

  [theme.breakpoints.up('md')]: {
    width: '90%',
    height: '90%',
  },

  [theme.breakpoints.up('lg')]: {
    width: '90%',
    maxHeight: '840px',
  },
}));

const ContentContainer = styled('div')({
  display: 'flex',
  width: '50%',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '2rem 8rem 2rem 2rem',
});

const MarketingContainer = styled('div')({
  display: 'flex',
  width: '50%',
  position: 'relative',

  '&:before': {
    content: '""',
    width: '50vw',
    height: 'calc(100% + 40px)',
    position: 'absolute',
    top: -20,
    left: 0,
    backgroundImage:
      'linear-gradient(90deg, rgba(45, 45, 82, 0.4), transparent)',
  },
});

const MarketingStylesContainer = styled('div')({
  display: 'flex',
  padding: '3rem',
  alignItems: 'flex-end',
  position: 'relative',
});

const ResponsiveImageBox = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '80%',
  height: 0,
  paddingBottom: '93.25%', // 16:9 aspect ratio
  overflow: 'hidden',
  borderTopRightRadius: '8px',

  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const MarketingMessage = styled('div')({
  position: 'relative',
  zIndex: 1,
  textAlign: 'right',
});

const MarketingAction = styled(CbButton)(({ theme }: any) => ({
  background: 'transparent',
  color: '#fff',
  border: `1px solid ${theme.config.colors.cowbellBlue}`,
  transition: 'all 0.2s',
  '&:hover': {
    background: theme.config.colors.cowbellBlue,
    border: `1px solid transparent`,
  },
}));
